/**
 * Giftera × Ella — giftera.js (global, încărcat cu defer din layout/theme.liquid).
 *
 * Vanilla, fără dependențe. Din Ella se folosește doar API-ul public:
 *   window.sharedFunctions.updateSidebarCart(cart), Shopify.getCart(cb),
 *   evenimentul `cart-update` pe document, clasa `cart-sidebar-show` pe <body>.
 *
 * Module (fiecare se înregistrează în init() și nu face nimic dacă elementele lipsesc):
 *   - header: starea „lipită” a navigației (.gf-nav.is-stuck);
 *   - sertarul de meniu de pe mobil (body.gf-menu-open);
 *   - căutarea de pe mobil: lupa din header deschide formularul ca rând al doilea (.gf-hd.is-search-open);
 *   - mega-meniu: Esc îl închide, prima atingere îl deschide pe ecrane tactile late;
 *   - bulina coșului ascunsă la 0;
 *   - sertarul de coș ca dialog: focus la deschidere/închidere, Esc îl închide;
 *   - coșul: rezervă pentru deschiderea sertarului Ella când Ella nu și-a legat clickul;
 *   - butonul „sus”;
 *   - Giftera.toast(text);
 *   - adăugarea în coș de pe cardul de produs ([data-gf-card-add], snippets/giftera-card-add.liquid);
 *   - derulare lină la ancorele de pe aceeași pagină (ex. /#cutii pe homepage).
 *
 * De adăugat de pașii următori (docs/ARHITECTURA.md §5): sincronizarea add-on-ului `card-personalizat`.
 */
(function () {
  'use strict';

  /** Spațiul de nume global al codului Giftera (folosit și de giftera-product.js). */
  var Giftera = (window.Giftera = window.Giftera || {});

  var MOBILE_MAX = 900; // pragul din machetă: sub el .gf-nav e sertar
  var TOTOP_AFTER = 700;
  var STUCK_EXTRA = 60;
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  var doc = document;
  var root = doc.documentElement;
  var started = false;
  var ticking = false;
  var lastPointerType = '';
  var toastTimer = 0;
  var cartDrawerObserver = null;
  var filterDrawerObserver = null;
  var cartDrawerOpener = null;
  var bubbleObserver = null;
  var megasWarm = false;

  /* ---------------------------------------------------------------------- */
  /* utilitare                                                               */
  /* ---------------------------------------------------------------------- */

  function $(selector, scope) {
    return (scope || doc).querySelector(selector);
  }

  function $all(selector, scope) {
    return Array.prototype.slice.call((scope || doc).querySelectorAll(selector));
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function scrollBehavior() {
    return prefersReducedMotion() ? 'auto' : 'smooth';
  }

  function isMobile() {
    return window.innerWidth <= MOBILE_MAX;
  }

  /** Cel mai apropiat strămoș (sau elementul însuși) care se potrivește; null pentru noduri fără closest. */
  function closest(target, selector) {
    return target && typeof target.closest === 'function' ? target.closest(selector) : null;
  }

  /* ---------------------------------------------------------------------- */
  /* scroll: nav lipit + butonul „sus”                                       */
  /* ---------------------------------------------------------------------- */

  function onScroll() {
    ticking = false;
    var y = window.scrollY || window.pageYOffset || 0;
    var nav = $('[data-gf-nav]');
    var header = $('[data-gf-header]');
    var toTop = $('[data-gf-totop]');

    if (nav && header) {
      nav.classList.toggle('is-stuck', !isMobile() && y > header.offsetHeight + STUCK_EXTRA);
    }
    if (toTop) {
      toTop.classList.toggle('is-visible', y > TOTOP_AFTER);
    }
  }

  function requestScrollUpdate() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(onScroll);
  }

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: scrollBehavior() });
  }

  /* ---------------------------------------------------------------------- */
  /* sertarul de meniu (≤900px)                                              */
  /* ---------------------------------------------------------------------- */

  function isMenuOpen() {
    return doc.body.classList.contains('gf-menu-open');
  }

  function openMenu() {
    var nav = $('[data-gf-nav]');
    if (!nav || !isMobile() || isMenuOpen()) return;

    closeSearch(false);
    doc.body.classList.add('gf-menu-open');
    root.classList.add('gf-lock');
    $all('[data-gf-menu-open]').forEach(function (button) {
      button.setAttribute('aria-expanded', 'true');
    });

    var closeButton = $('.gf-nav__close', nav);
    focusQuiet(closeButton);
  }

  /**
   * Focus fără derulare: elementele din bara lipită stau sub headerul lipicios, iar un focus()
   * simplu face browserul să „aducă în vedere” elementul și pagina sare în sus.
   * @param {HTMLElement | null | undefined} el
   */
  function focusQuiet(el) {
    if (!el || !el.focus) return;
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }

  /**
   * @param {boolean} [restoreFocus] - întoarce focusul pe burger (Esc, buton, scrim)
   */
  function closeMenu(restoreFocus) {
    if (!isMenuOpen()) return;

    doc.body.classList.remove('gf-menu-open');
    root.classList.remove('gf-lock');
    var burgers = $all('[data-gf-menu-open]');
    burgers.forEach(function (button) {
      button.setAttribute('aria-expanded', 'false');
    });
    if (restoreFocus) focusQuiet(burgers[0]);
  }

  /** Ține Tab-ul în sertarul deschis. */
  function trapFocus(event) {
    var nav = $('[data-gf-nav]');
    if (!nav) return;

    var items = $all(FOCUSABLE, nav).filter(function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0;
    });
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];
    var active = doc.activeElement;

    if (!nav.contains(active)) {
      event.preventDefault();
      focusQuiet(first);
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      focusQuiet(last);
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      focusQuiet(first);
    }
  }

  /* ---------------------------------------------------------------------- */
  /* căutarea de pe mobil (≤900px)                                           */
  /* ---------------------------------------------------------------------- */

  /**
   * Lupa din header ([data-gf-search-toggle], valoarea = id-ul formularului), formularul și câmpul lui.
   * @returns {{ header: HTMLElement, toggle: HTMLElement, form: HTMLElement, input: HTMLElement | null } | null}
   */
  function searchParts() {
    var header = $('[data-gf-header]');
    var toggle = header ? $('[data-gf-search-toggle]', header) : null;
    var form = toggle ? doc.getElementById(toggle.getAttribute('data-gf-search-toggle') || '') : null;
    if (!header || !toggle || !form) return null;
    return { header: header, toggle: toggle, form: form, input: $('[data-gf-search-input]', form) };
  }

  /** Fără JS lupa e un link spre pagina de căutare; cu JS devine butonul care deschide formularul. */
  function setupSearchToggle() {
    var parts = searchParts();
    if (!parts) return;
    parts.toggle.setAttribute('role', 'button');
    parts.toggle.setAttribute('aria-controls', parts.form.id);
    parts.toggle.setAttribute('aria-expanded', parts.header.classList.contains('is-search-open') ? 'true' : 'false');
  }

  function isSearchOpen() {
    var header = $('[data-gf-header]');
    return !!header && header.classList.contains('is-search-open');
  }

  /**
   * Textul de ajutor lung nu încape în câmpul de pe telefon, iar un câmp cu focus nu îl termină cu „…”
   * (rămâne tăiat la mijlocul cuvântului). Cât e deschis rândul de căutare, câmpul arată textul scurt
   * din data-gf-placeholder-short; la închidere revine la cel lung, folosit pe desktop.
   * @param {HTMLElement | null} input
   * @param {boolean} useShort
   */
  function setSearchPlaceholder(input, useShort) {
    var shortText = input ? input.getAttribute('data-gf-placeholder-short') : '';
    if (!shortText) return;

    if (useShort) {
      if (!input.hasAttribute('data-gf-placeholder-long')) {
        input.setAttribute('data-gf-placeholder-long', input.getAttribute('placeholder') || '');
      }
      input.setAttribute('placeholder', shortText);
    } else if (input.hasAttribute('data-gf-placeholder-long')) {
      input.setAttribute('placeholder', input.getAttribute('data-gf-placeholder-long'));
      input.removeAttribute('data-gf-placeholder-long');
    }
  }

  /**
   * Deschide rândul de căutare și pune focusul în câmp, în același gest (altfel iOS nu ridică tastatura).
   * @returns {boolean} true dacă rândul s-a deschis
   */
  function openSearch() {
    var parts = searchParts();
    if (!parts || !isMobile()) return false;

    setSearchPlaceholder(parts.input, true);
    parts.header.classList.add('is-search-open');
    parts.toggle.setAttribute('aria-expanded', 'true');
    focusQuiet(parts.input);
    return true;
  }

  /**
   * @param {boolean} [restoreFocus] - întoarce focusul pe lupă (a doua atingere, Esc)
   */
  function closeSearch(restoreFocus) {
    var parts = searchParts();
    if (!parts || !isSearchOpen()) return;

    var hadFocus = parts.form.contains(doc.activeElement);
    parts.header.classList.remove('is-search-open');
    parts.toggle.setAttribute('aria-expanded', 'false');
    setSearchPlaceholder(parts.input, false);
    if (restoreFocus) focusQuiet(parts.toggle);
    else if (hadFocus && doc.activeElement && doc.activeElement.blur) doc.activeElement.blur();
  }

  /* ---------------------------------------------------------------------- */
  /* mega-meniu                                                              */
  /* ---------------------------------------------------------------------- */

  function closeTouchMegas(except) {
    $all('[data-gf-mega].is-open').forEach(function (item) {
      if (item !== except) item.classList.remove('is-open');
    });
  }

  /** Esc: ascunde mega-ul deschis prin hover / focus, până când cursorul sau focusul pleacă de pe el. */
  function dismissMegas() {
    var dismissed = false;
    $all('[data-gf-mega]').forEach(function (item) {
      if (item.classList.contains('is-open') || item.matches(':hover') || item.contains(doc.activeElement)) {
        item.classList.remove('is-open');
        item.classList.add('is-closed');
        dismissed = true;
      }
    });
    return dismissed;
  }

  /**
   * Pozele din mega sunt loading="lazy" într-un panou display:none, deci s-ar încărca abia la prima
   * deschidere (panou gol o clipă). Le cerem din timp: la prima apropiere de meniu sau când pagina e liberă.
   */
  function warmMegas() {
    if (megasWarm || isMobile()) return;
    megasWarm = true;
    $all('.gf-mega img[loading="lazy"]').forEach(function (img) {
      img.loading = 'eager';
    });
  }

  function onMegaLeave(event) {
    var item = closest(event.target, '[data-gf-mega]');
    if (!item || !item.classList.contains('is-closed')) return;
    if (event.type === 'focusout' && item.contains(event.relatedTarget)) return;
    item.classList.remove('is-closed');
  }

  /* ---------------------------------------------------------------------- */
  /* coș                                                                     */
  /* ---------------------------------------------------------------------- */

  /** Ella scrie mereu numărul (inclusiv 0); macheta arată bulina doar când coșul are ceva. */
  function syncBubbles() {
    $all('.gf-bub').forEach(function (bubble) {
      var counter = $('[data-cart-count]', bubble);
      var text = counter ? (counter.textContent || '').trim() : '';
      bubble.classList.toggle('is-empty', text === '' || text === '0');
    });
  }

  function watchBubbles() {
    if (bubbleObserver) bubbleObserver.disconnect();
    if (!('MutationObserver' in window)) return;

    bubbleObserver = new MutationObserver(syncBubbles);
    $all('.gf-bub [data-cart-count]').forEach(function (counter) {
      bubbleObserver.observe(counter, { childList: true, characterData: true, subtree: true });
    });
  }

  /**
   * Rezervă: Ella leagă clickul direct pe [data-cart-sidebar] la pornire și oprește propagarea.
   * Dacă evenimentul ajunge totuși până la document (header reîncărcat în editorul de temă),
   * deschidem aici același sertar. Fără sertar în pagină, linkul duce normal la /cart.
   */
  function openCartFallback(event) {
    if (!doc.getElementById('halo-cart-sidebar')) return;
    if (doc.body.classList.contains('template-cart')) return;

    event.preventDefault();
    doc.body.classList.add('cart-sidebar-show');

    if (window.Shopify && typeof window.Shopify.getCart === 'function' && window.sharedFunctions && typeof window.sharedFunctions.updateSidebarCart === 'function') {
      window.Shopify.getCart(function (cart) {
        window.sharedFunctions.updateSidebarCart(cart);
      });
    }
  }

  /**
   * Sertarul de coș ca dialog: la deschidere (clasa `cart-sidebar-show` pe <body>, pusă de Ella sau de noi)
   * focusul trece pe butonul de închidere; la închidere se întoarce pe elementul de pe care a plecat.
   * @returns {void}
   */
  function watchCartDrawer() {
    if (cartDrawerObserver || !('MutationObserver' in window) || !doc.body) return;
    var wasOpen = doc.body.classList.contains('cart-sidebar-show');

    cartDrawerObserver = new MutationObserver(function () {
      var open = doc.body.classList.contains('cart-sidebar-show');
      if (open === wasOpen) return;
      wasOpen = open;

      if (open) {
        // rândul de căutare de pe mobil nu rămâne deschis sub sertar (Ella oprește clickul pe coș înainte de onClick)
        closeSearch(false);
        var drawer = doc.getElementById('halo-cart-sidebar');
        if (!drawer) return;
        if (!drawer.contains(doc.activeElement)) cartDrawerOpener = doc.activeElement;
        var target = $('[data-close-cart-sidebar]', drawer) || drawer;
        // sertarul intră cu o tranziție; cât e încă `visibility: hidden`, focus() nu prinde
        var tries = 0;
        (function focusDrawer() {
          if (!doc.body.classList.contains('cart-sidebar-show')) return;
          if (drawer.contains(doc.activeElement)) return;
          try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
          if (!drawer.contains(doc.activeElement) && tries++ < 10) window.setTimeout(focusDrawer, 60);
        })();
        return;
      }

      var opener = cartDrawerOpener;
      cartDrawerOpener = null;
      var drawerEl = doc.getElementById('halo-cart-sidebar');
      var inside = drawerEl && drawerEl.contains(doc.activeElement);
      if (opener && opener !== doc.body && doc.contains(opener) && opener.focus && (inside || doc.activeElement === doc.body)) {
        focusQuiet(opener);
      }
    });
    cartDrawerObserver.observe(doc.body, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * Sertarul de filtre (sub 1025px; clasa `open-mobile-sidebar` pe <body>, pusă de Ella): butonul care îl
   * deschide își ține `aria-expanded`, focusul trece pe butonul de închidere și, la închidere, se întoarce.
   * @returns {void}
   */
  function watchFilterDrawer() {
    if (filterDrawerObserver || !('MutationObserver' in window) || !doc.body) return;
    var wasOpen = doc.body.classList.contains('open-mobile-sidebar');

    filterDrawerObserver = new MutationObserver(function () {
      var open = doc.body.classList.contains('open-mobile-sidebar');
      if (open === wasOpen) return;
      wasOpen = open;

      var opener = $('.toolbar [data-sidebar]');
      var drawer = doc.getElementById('halo-sidebar');
      if (opener) opener.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!drawer) return;

      if (open) {
        var target = $('[data-close-sidebar]', drawer);
        if (!target) return;
        // sertarul intră cu o tranziție; cât e încă `visibility: hidden`, focus() nu prinde
        var tries = 0;
        (function focusDrawer() {
          if (!doc.body.classList.contains('open-mobile-sidebar')) return;
          if (drawer.contains(doc.activeElement)) return;
          try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
          if (!drawer.contains(doc.activeElement) && tries++ < 10) window.setTimeout(focusDrawer, 60);
        })();
        return;
      }

      if (opener && opener.focus && (drawer.contains(doc.activeElement) || doc.activeElement === doc.body)) focusQuiet(opener);
    });
    filterDrawerObserver.observe(doc.body, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------------------------------------------------------------------- */
  /* toast                                                                   */
  /* ---------------------------------------------------------------------- */

  /**
   * Arată un mesaj scurt în regiunea live de jos.
   * @param {string} text
   * @param {number} [duration=2200] - milisecunde
   * @returns {void}
   */
  function toast(text, duration) {
    var el = $('[data-gf-toast]');
    if (!el) {
      if (!doc.body) return;
      el = doc.createElement('div');
      el.className = 'gf-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.setAttribute('aria-atomic', 'true');
      el.setAttribute('data-gf-toast', '');
      doc.body.appendChild(el);
    }

    window.clearTimeout(toastTimer);
    el.textContent = '';
    window.requestAnimationFrame(function () {
      el.textContent = String(text == null ? '' : text);
      el.classList.add('is-on');
    });
    toastTimer = window.setTimeout(function () {
      el.classList.remove('is-on');
    }, typeof duration === 'number' ? duration : 2200);
  }

  /* ---------------------------------------------------------------------- */
  /* adăugarea în coș de pe cardul de produs                                 */
  /* ---------------------------------------------------------------------- */

  var CARD_MODEL_KEY = 'properties[Model lănțișor]';
  var CARD_CODE_KEY = 'properties[_Cod lănțișor]';

  function cartAddUrl() {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return base + 'cart/add.js';
  }

  /**
   * Starea de încărcare a benzii „Adaugă în coș”. Butonul nu primește `disabled` (ar pierde focusul);
   * dublul click e oprit de aria-busy.
   * @param {HTMLButtonElement} button
   * @param {boolean} busy
   */
  function setCardAddBusy(button, busy) {
    var label = $('[data-gf-card-add-text]', button) || button;
    var form = closest(button, '[data-gf-card-add-form]');

    if (busy) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('data-gf-label', label.textContent);
      if (button.getAttribute('data-gf-adding')) label.textContent = button.getAttribute('data-gf-adding');
    } else {
      button.removeAttribute('aria-busy');
      if (button.hasAttribute('data-gf-label')) {
        label.textContent = button.getAttribute('data-gf-label');
        button.removeAttribute('data-gf-label');
      }
    }
    if (form) form.classList.toggle('is-busy', busy);
  }

  /** Scrie numărul de articole în bulinele din pagină (când sertarul Ella nu e disponibil). */
  function writeCartCount(count) {
    $all('[data-cart-count]').forEach(function (counter) {
      counter.textContent = String(count);
    });
    syncBubbles();
  }

  /** Recitește coșul și actualizează doar bulina (fără să deschidă sertarul). */
  function refreshCartCount() {
    fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        writeCartCount(cart.item_count);
        doc.dispatchEvent(new CustomEvent('cart-update', { detail: cart }));
      })
      .catch(function () {
        /* bulina se corectează la următoarea încărcare de pagină */
      });
  }

  /**
   * După o adăugare reușită: la fel ca Ella (theme.js, actionAddToCart, ramura `quick_cart`) — pe pagina
   * de coș reîncarcă pagina, altfel citește coșul, deschide sertarul și îl redesenează (updateSidebarCart
   * scrie și bulina și emite `cart-update`). Fără sertar: actualizează bulina și anunță în toast.
   * @param {HTMLButtonElement} button
   */
  function afterCardAdd(button) {
    var body = doc.body;
    var hasEllaCart = !!(window.Shopify && typeof window.Shopify.getCart === 'function' && window.sharedFunctions && typeof window.sharedFunctions.updateSidebarCart === 'function');
    var drawerOn = !window.quick_cart || window.quick_cart.show !== false;
    var afterType = window.after_add_to_cart && window.after_add_to_cart.type;

    if (body.classList.contains('template-cart') || afterType === 'cart') {
      window.location.assign((window.routes && window.routes.cart) || '/cart');
      return;
    }

    if (hasEllaCart && drawerOn && doc.getElementById('halo-cart-sidebar')) {
      window.Shopify.getCart(function (cart) {
        body.classList.add('cart-sidebar-show');
        window.sharedFunctions.updateSidebarCart(cart);
      });
      return;
    }

    refreshCartCount();
    if (button.getAttribute('data-gf-added')) toast(button.getAttribute('data-gf-added'));
  }

  /**
   * Adaugă setul de pe card în coș, cu „Model lănțișor” + „_Cod lănțișor” (decizia 4).
   * POST prin fetch (îl vede și învelișul 2Performant). La eroare (422 etc.) arată descrierea
   * primită de la Shopify și reactivează butonul.
   * @param {HTMLButtonElement} button - [data-gf-card-add]
   * @returns {boolean} false dacă formularul nu e complet (clickul rămâne al browserului)
   */
  function addFromCard(button) {
    var form = closest(button, 'form');
    if (!form || typeof window.fetch !== 'function' || typeof window.FormData !== 'function') return false;
    if (button.getAttribute('aria-busy') === 'true') return true;

    var data = new FormData(form);
    // un set nu pleacă niciodată fără model: dacă lipsește ceva, mergem pe pagina produsului
    if (!data.get('id') || !data.get(CARD_MODEL_KEY) || !data.get(CARD_CODE_KEY)) {
      var card = closest(button, '.product-item');
      var link = card ? $('a.card-link[href], a.card-title[href]', card) : null;
      if (link) window.location.assign(link.href);
      return true;
    }

    var fallbackError = (window.cartStrings && window.cartStrings.error) || '';
    setCardAddBusy(button, true);

    fetch(cartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: data
    })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return {};
          })
          .then(function (json) {
            return { ok: response.ok, json: json || {} };
          });
      })
      .then(function (result) {
        setCardAddBusy(button, false);
        if (!result.ok || result.json.status) {
          toast(result.json.description || result.json.message || fallbackError, 4000);
          // un 422 de stoc poate adăuga totuși o parte din cantitate → bulina rămâne corectă
          refreshCartCount();
          return;
        }
        afterCardAdd(button);
      })
      .catch(function () {
        setCardAddBusy(button, false);
        toast(fallbackError, 4000);
      });

    return true;
  }

  /* ---------------------------------------------------------------------- */
  /* ancore pe aceeași pagină                                                */
  /* ---------------------------------------------------------------------- */

  /**
   * Linkuri de forma „/cale#id” spre pagina curentă (ex. /#cutii pe homepage) derulează lin, fără
   * reîncărcare. Linkurile „#id” simple sunt lăsate browserului (Ella le folosește pentru ferestre
   * și taburi), cu excepția celor din header/nav sau marcate cu data-gf-scroll.
   * @returns {boolean} true dacă a preluat clickul
   */
  function handleHashLink(link, event) {
    var raw = link.getAttribute('href') || '';
    var hashAt = raw.indexOf('#');
    if (hashAt === -1 || hashAt === raw.length - 1) return false;
    if (link.target && link.target !== '_self') return false;
    if (hashAt === 0 && !link.hasAttribute('data-gf-scroll') && !closest(link, '.gf-nav, .gf-hd, [data-gf-scroll]')) return false;

    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (error) {
      return false;
    }
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.search !== window.location.search) return false;

    var id;
    try {
      id = decodeURIComponent(url.hash.slice(1));
    } catch (error) {
      id = url.hash.slice(1);
    }
    var target = id ? doc.getElementById(id) : null;
    if (!target) return false;

    event.preventDefault();
    closeMenu(false);
    closeSearch(false);
    target.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });

    if (window.history && typeof window.history.pushState === 'function' && window.location.hash !== url.hash) {
      window.history.pushState(null, '', url.hash);
    }
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    return true;
  }

  /* ---------------------------------------------------------------------- */
  /* evenimente (delegate, legate o singură dată)                            */
  /* ---------------------------------------------------------------------- */

  function onClick(event) {
    if (event.defaultPrevented) return;
    var target = event.target;

    if (closest(target, '[data-gf-menu-open]')) {
      event.preventDefault();
      if (isMenuOpen()) closeMenu(true);
      else openMenu();
      return;
    }

    if (closest(target, '[data-gf-menu-close]')) {
      event.preventDefault();
      closeMenu(true);
      return;
    }

    if (closest(target, '[data-gf-totop]')) {
      event.preventDefault();
      scrollToTop();
      return;
    }

    // banda „Adaugă în coș” de pe cardul de produs
    var cardAdd = closest(target, '[data-gf-card-add]');
    if (cardAdd) {
      if (addFromCard(cardAdd)) event.preventDefault();
      return;
    }

    if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    // lupa de pe mobil: deschide / închide rândul de căutare din header (fără JS e link spre pagina de căutare)
    if (closest(target, '[data-gf-search-toggle]')) {
      if (isSearchOpen()) {
        event.preventDefault();
        closeSearch(true);
      } else if (openSearch()) {
        event.preventDefault();
      }
      return;
    }

    // lupa din bara lipită: urcă la câmpul de căutare din header (fără JS e link spre pagina de căutare)
    var searchTool = closest(target, '[data-gf-search-focus]');
    if (searchTool) {
      var input = $('[data-gf-search-input]');
      if (input && input.offsetParent !== null) {
        event.preventDefault();
        scrollToTop();
        input.focus({ preventScroll: true });
      }
      return;
    }

    var megaItem = closest(target, '[data-gf-mega]');
    var megaTrigger = closest(target, '.gf-nav__link');

    // sertarul mobil: „Pentru ea” / „Pentru el” deschid și închid submeniul cu relații (unul singur deschis)
    if (megaItem && megaTrigger && megaTrigger.parentNode === megaItem && isMobile() && megaItem.hasAttribute('data-gf-sub') && $('.gf-mega__list', megaItem)) {
      event.preventDefault();
      var subOpen = !megaItem.classList.contains('is-sub-open');
      $all('[data-gf-sub].is-sub-open').forEach(function (item) {
        item.classList.remove('is-sub-open');
        var trigger = $('.gf-nav__link', item);
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
      megaItem.classList.toggle('is-sub-open', subOpen);
      megaTrigger.setAttribute('aria-expanded', subOpen ? 'true' : 'false');
      return;
    }

    // ecran tactil lat: prima atingere pe „Pentru ea” deschide mega-ul, a doua urmează linkul
    if (megaItem && megaTrigger && megaTrigger.parentNode === megaItem && !isMobile() && lastPointerType === 'touch' && $('.gf-mega', megaItem)) {
      if (!megaItem.classList.contains('is-open')) {
        event.preventDefault();
        closeTouchMegas(megaItem);
        megaItem.classList.remove('is-closed');
        megaItem.classList.add('is-open');
        return;
      }
    } else if (!megaItem) {
      closeTouchMegas(null);
    }

    var cartLink = closest(target, '.gf-ibtn--cart[data-cart-sidebar]');
    if (cartLink) {
      closeMenu(false);
      openCartFallback(event);
      return;
    }

    var link = closest(target, 'a[href]');
    if (!link) return;

    if (handleHashLink(link, event)) return;

    // orice link din sertar îl închide (pagina se schimbă oricum; contează la ancore și la „înapoi”)
    if (isMenuOpen() && closest(link, '[data-gf-nav]')) closeMenu(false);
  }

  function onKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      if (isMenuOpen()) {
        event.preventDefault();
        closeMenu(true);
        return;
      }
      // sertarul de filtre și foaia de sortare (sub 1025px) nu au Esc în Ella: apăsăm butonul lor de închidere
      if (document.body.classList.contains('open-mobile-sidebar')) {
        var closeSidebar = document.querySelector('#halo-sidebar [data-close-sidebar]');
        if (closeSidebar) {
          event.preventDefault();
          closeSidebar.click();
          // focusul se întoarce pe butonul de filtre din watchFilterDrawer()
          return;
        }
      }
      if (document.body.classList.contains('toolbar-modal-open')) {
        var closeSort = document.querySelector('[data-sorting][data-mobile] .close-mobile-modal');
        if (closeSort) {
          event.preventDefault();
          // butonul e un <svg> (fără .click()), deci trimitem evenimentul
          closeSort.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
          var sortButton = document.querySelector('.toolbar [data-toggle][data-mobile="mobile"]');
          focusQuiet(/** @type {HTMLElement | null} */ (sortButton));
          return;
        }
      }
      // sertarul de coș (Ella) nu are Esc: apăsăm butonul lui de închidere; focusul îl redă watchCartDrawer()
      if (document.body.classList.contains('cart-sidebar-show') && !document.body.classList.contains('has-warning')) {
        var closeCart = document.querySelector('#halo-cart-sidebar [data-close-cart-sidebar]');
        if (closeCart) {
          event.preventDefault();
          closeCart.click();
          return;
        }
      }
      // rândul de căutare de pe mobil: după sertare, care stau deasupra lui
      if (isSearchOpen() && isMobile()) {
        event.preventDefault();
        closeSearch(true);
        return;
      }
      dismissMegas();
      return;
    }

    if (event.key === 'Tab' && isMenuOpen()) {
      trapFocus(event);
      return;
    }

    // lupa de pe mobil are role="button": Space o apasă, ca pe un buton
    if ((event.key === ' ' || event.key === 'Spacebar') && closest(event.target, '[data-gf-search-toggle][role="button"]')) {
      event.preventDefault();
      if (isSearchOpen()) closeSearch(true);
      else openSearch();
    }
  }

  /** Trimiterea formularului de căutare din header închide rândul (contează la „înapoi” din bfcache). */
  function onSubmit(event) {
    if (closest(event.target, '[data-gf-header] .gf-search')) closeSearch(false);
  }

  function onResize() {
    if (!isMobile()) {
      closeMenu(false);
      closeSearch(false);
    }
    requestScrollUpdate();
  }

  /** Reface starea care depinde de elementele din pagină (și după reîncărcarea secțiunii în editor). */
  function refresh() {
    megasWarm = false;
    setupSearchToggle();
    syncBubbles();
    watchBubbles();
    onScroll();
  }

  /**
   * Punctul unic de pornire. Rulează o singură dată, după ce DOM-ul e gata.
   * @returns {void}
   */
  function init() {
    if (started) {
      refresh();
      return;
    }
    started = true;

    window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    doc.addEventListener('click', onClick);
    doc.addEventListener('keydown', onKeydown);
    doc.addEventListener('submit', onSubmit);
    doc.addEventListener('pointerdown', function (event) {
      lastPointerType = event.pointerType || '';
    }, { passive: true });
    doc.addEventListener('mouseout', function (event) {
      var item = closest(event.target, '[data-gf-mega]');
      if (item && !item.contains(event.relatedTarget)) onMegaLeave(event);
    }, { passive: true });
    doc.addEventListener('focusout', onMegaLeave);
    doc.addEventListener('pointerover', function (event) {
      if (!megasWarm && closest(event.target, '[data-gf-nav]')) warmMegas();
    }, { passive: true });
    doc.addEventListener('focusin', function (event) {
      if (!megasWarm && closest(event.target, '[data-gf-nav]')) warmMegas();
    });
    window.addEventListener('load', function () {
      if ('requestIdleCallback' in window) window.requestIdleCallback(warmMegas, { timeout: 4000 });
      else window.setTimeout(warmMegas, 2500);
    }, { once: true });
    doc.addEventListener('cart-update', function () {
      // Ella a scris deja numărul în [data-cart-count]
      syncBubbles();
    });
    doc.addEventListener('shopify:section:load', refresh);
    window.addEventListener('pageshow', function (event) {
      // revenire din bfcache: sertarul nu rămâne deschis
      if (event.persisted) {
        closeMenu(false);
        closeSearch(false);
      }
    });

    watchCartDrawer();
    watchFilterDrawer();
    refresh();
  }

  Giftera.init = init;
  Giftera.toast = toast;
  Giftera.openMenu = openMenu;
  Giftera.closeMenu = closeMenu;
  Giftera.syncCartBubbles = syncBubbles;
  Giftera.addFromCard = addFromCard;

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
